import { HttpErrorResponse } from '@angular/common/http';
import { ApiError, ApiErrorDto } from '@common/app/models/api-error.model';

function isApiErrorDto(body: unknown): body is ApiErrorDto {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return typeof candidate['status'] === 'number' && typeof candidate['message'] === 'string';
}

function extractMessageFromBody(body: unknown): string | null {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('{')) {
      try {
        return extractMessageFromBody(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (!body || typeof body !== 'object') {
    return null;
  }

  const candidate = body as Record<string, unknown>;
  const directMessage = candidate['message'];
  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage.trim();
  }

  const detail = candidate['detail'];
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  const error = candidate['error'];
  if (typeof error === 'string' && error.trim() && error !== 'Internal Server Error') {
    return error.trim();
  }

  return null;
}

function fallbackUserMessage(status: number): string {
  if (status === 0) {
    return 'Não foi possível conectar à API. Verifique sua conexão ou se o serviço está em execução.';
  }
  if (status === 400) {
    return 'Requisição inválida. Verifique os dados informados.';
  }
  if (status === 401) {
    return 'Sessão expirada ou credenciais inválidas. Faça login novamente.';
  }
  if (status === 403) {
    return 'Você não tem permissão para realizar esta ação.';
  }
  if (status === 404) {
    return 'Recurso não encontrado.';
  }
  if (status >= 500) {
    return 'Erro interno no servidor. Tente novamente em instantes.';
  }
  return 'Não foi possível concluir a operação. Tente novamente.';
}

export function parseApiError(err: unknown): ApiError {
  if (err instanceof HttpErrorResponse) {
    const body = err.error;
    const extractedMessage = extractMessageFromBody(body);

    if (isApiErrorDto(body)) {
      return {
        status: body.status,
        code: body.error ?? `HTTP_${body.status}`,
        message: body.message,
        userMessage: body.message || extractedMessage || fallbackUserMessage(body.status),
        path: body.path,
      };
    }

    const status = err.status;
    return {
      status,
      code: `HTTP_${status}`,
      message: extractedMessage ?? (typeof body === 'string' ? body : err.message),
      userMessage: extractedMessage || fallbackUserMessage(status),
      path: err.url ?? undefined,
    };
  }

  if (err && typeof err === 'object' && 'userMessage' in err) {
    return err as ApiError;
  }

  if (err instanceof Error) {
    return {
      status: 0,
      code: 'UNKNOWN_ERROR',
      message: err.message,
      userMessage: fallbackUserMessage(0),
    };
  }

  return {
    status: 0,
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error',
    userMessage: fallbackUserMessage(0),
  };
}

/** Mensagens amigáveis para a tela de login. */
export function parseLoginError(err: unknown): string {
  const apiError = parseApiError(err);
  if (apiError.status === 401 || apiError.status === 400) {
    return 'Login ou senha inválidos. Verifique suas credenciais.';
  }
  if (apiError.status === 404) {
    return 'Endpoint de login não encontrado. Verifique se a API de autenticação está em execução.';
  }
  if (apiError.status === 0) {
    return 'Não foi possível conectar à API. Verifique se a API está em execução (dev) ou se o proxy do ng serve está ativo (homol local).';
  }
  return apiError.userMessage || 'Não foi possível entrar. Tente novamente em instantes.';
}
